import { useEffect, useState } from 'react';
import { authedFetch, errorFromResponse } from './api';
import { trackLead } from './metaPixel';

/**
 * "Stay in the loop": the email list (functions/emailList.js).
 *
 * One small form in three places, each telling the server where it was
 * given: 'gate' when a guest's free questions for the window are used up,
 * 'card' from the questions-left line under the box, 'sidebar' from the
 * menu. Giving an email lifts a guest's window from 6 questions to 40.
 *
 * This browser remembers only that it gave an address (never the address
 * itself), so the menu can say "You’re on the list".
 */

// Shown with every form, and stored with each sign-up as what the person
// agreed to: `${PROMISE} ${FINE_PRINT_LEAD} Privacy Policy.` is
// CONSENT_TEXT in functions/emailList.js (scripts/check-limits.js checks).
export const PROMISE = 'We’ll let you know as AI Rob gets better. No spam, and you can unsubscribe anytime.';
export const FINE_PRINT_LEAD = 'By adding your email, you agree to our';
export const JOINED = 'Thanks! You’re in.';

const ON_LIST_KEY = 'apr-on-list';
let onList = null;
const listeners = new Set();

function readOnList() {
  try { return localStorage.getItem(ON_LIST_KEY) === '1'; } catch (e) { return false; }
}

export function isOnList() {
  if (onList === null) onList = readOnList();
  return onList;
}

function rememberOnList() {
  onList = true;
  try { localStorage.setItem(ON_LIST_KEY, '1'); } catch (e) { /* storage blocked: this visit only */ }
  listeners.forEach((fn) => fn(true));
}

export function useOnList() {
  const [value, setValue] = useState(isOnList);
  useEffect(() => {
    setValue(isOnList());
    listeners.add(setValue);
    return () => listeners.delete(setValue);
  }, []);
  return value;
}

/** A light check before asking the server, which has the last word. */
export function looksLikeEmail(text) {
  const value = String(text || '').trim();
  return value.length <= 254 && /^[^\s@,;<>()]+@[^\s@,;<>()]+\.[^\s@,;<>().]{2,}$/.test(value);
}

/** Adds the address; throws the server's own (kind) words when it can't. */
export async function joinList(email, source) {
  const response = await authedFetch('joinList', { email: String(email || '').trim(), source });
  if (!response.ok) throw await errorFromResponse(response);
  rememberOnList();
  trackLead(); // Meta "Lead", no parameters, only after the server said yes (utils/metaPixel.js)
}

/** What to say when it didn't work. The typed address always stays in the box. */
export function joinErrorText(err) {
  if (err?.safeMessage) return err.safeMessage;
  const msg = String(err?.message || '').toLowerCase();
  if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('load failed')) {
    return 'We couldn’t reach the server. Check your connection and try again.';
  }
  return 'That didn’t go through. Please try again in a moment.';
}
